import { GEEKS_USER } from "constants/profile";
import {
  OTHER_SURFACE_SCREENSHOT_TYPE,
  SCREEN_CAPTURE_SESSIONS_END_POINT,
  SCREENSHOT_IMPORT_STATUS,
  TRADE_CAPTURE_IMPORT_PREFS_KEY,
  TRADE_CAPTURE_OUTPUT,
  TRADE_CAPTURE_SOURCE,
  TRADE_COLUMNS,
} from "constants/tradeCapture";
import { normalizeActionedAtTimeToUtc } from "helpers/actionedAtTime";
import { getDataFromLocalStorage } from "helpers/generalHelper";

/** Legacy localStorage key from removed “resume capture” flow. */
export const LEGACY_TRADE_CAPTURE_LAST_SESSION_KEY = "tradeCapture_lastSessionId";

export const isErrorResponse = (res) => !res || res.status >= 400;

export const SCREENSHOT_MAX_BYTES = 10 * 1024 * 1024;

export const SCREENSHOT_MIME_TYPES = ["image/jpeg", "image/png", "image/webp"];

export const isOtherCallPutImport = (source) =>
  source === TRADE_CAPTURE_SOURCE.OTHER_CALL_PUT_IMPORT;

/** Add screenshot stays available until the session is imported or closed. */
export const canAddCallPutScreenshot = (status) =>
  status === SCREENSHOT_IMPORT_STATUS.DRAFT ||
  status === SCREENSHOT_IMPORT_STATUS.VALIDATED;

export const getScreenshotFileError = (file) => {
  if (!file || !SCREENSHOT_MIME_TYPES.includes(file.type)) {
    return "Please use a JPEG, PNG, or WebP image";
  }
  if (file.size > SCREENSHOT_MAX_BYTES) {
    return "File size must be less than 10MB";
  }
  return null;
};

export const isOtherSurfaceOutput = (output) =>
  output === TRADE_CAPTURE_OUTPUT.OTHER_SURFACE;

/**
 * Extra create-session fields. ICS blotter must omit `source` so the backend
 * keeps blotter OCR. Other Call/Put Import appends other_call_put_import.
 * The Pricer popup does not know the product, so product_contract_style_id
 * stays off the request and the backend reads it from the window title.
 * Other surface screenshot create reads the product from the chain image (OCR),
 * not the PCS tab open on the page. Preview/fill may send product_contract_style_id
 * when the user picks a target PCS. Never send target_user_id for that output.
 */
export const appendTradeCaptureCreateFields = (
  formData,
  { source, output } = {}
) => {
  if (!formData) return formData;
  if (isOtherSurfaceOutput(output)) {
    formData.delete("target_user_id");
    formData.append("screenshot_type", OTHER_SURFACE_SCREENSHOT_TYPE);
    formData.append("output", TRADE_CAPTURE_OUTPUT.OTHER_SURFACE);
    return formData;
  }
  if (!isOtherCallPutImport(source)) return formData;
  formData.append("source", TRADE_CAPTURE_SOURCE.OTHER_CALL_PUT_IMPORT);
  return formData;
};

const toLadderNumber = (value) => {
  if (value == null) return NaN;
  const cleaned = String(value).replace(/,/g, "").trim();
  return cleaned === "" ? NaN : Number(cleaned);
};

/** Mirrors the backend: strikes below ceil(UL) are Puts, the rest Calls. */
export const otherSurfaceCallOrPut = (strike, underlying) => {
  const ul = toLadderNumber(underlying);
  const k = toLadderNumber(strike);
  if (!Number.isFinite(ul) || ul <= 0 || !Number.isFinite(k)) return "";
  return k < Math.ceil(ul) ? "Put" : "Call";
};

/**
 * Ladder rows with C/P and premium recomputed from the UL, the way the fill
 * will read them. Row order is kept; the backend sorts on preview.
 */
export const withOtherSurfaceUnderlying = (rows, underlying) =>
  (Array.isArray(rows) ? rows : []).map((row) => {
    if (!row || typeof row !== "object") return row;
    const cp = otherSurfaceCallOrPut(row.strike, underlying);
    const premium =
      cp === "Call"
        ? row.call_settle ?? ""
        : cp === "Put"
          ? row.put_settle ?? ""
          : "";
    if (row.cp === cp && row.premium === premium) return row;
    return { ...row, cp, premium };
  });

/** Ladder rows have no Time column, so the grid keeps them as they are. */
export const keepLadderRows = (rows) => (Array.isArray(rows) ? rows : []);

/** Human-readable PCS label from create/OCR `product_contract_style`. */
export const formatProductContractStyleLabel = (pcs) => {
  if (!pcs || typeof pcs !== "object") return "";
  const commodity =
    pcs.commodity == null ? "" : String(pcs.commodity).trim();
  const contractStyle =
    pcs.contract_style == null ? "" : String(pcs.contract_style).trim();
  if (commodity && contractStyle) return `${commodity} — ${contractStyle}`;
  return commodity || contractStyle || "";
};

export const normalizeProductContractStyleIdValue = (value) => {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** Parsed UL, or null when it is not a positive number. */
export const parseOtherSurfaceUnderlying = (value) => {
  const n = toLadderNumber(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** Current grid rows as the add-screenshot `table_data` JSON string. */
export const buildAddScreenshotFormData = (file, rows) => {
  const formData = new FormData();
  formData.append("screenshot", file);
  formData.append(
    "table_data",
    JSON.stringify(Array.isArray(rows) ? rows : [])
  );
  return formData;
};

const stringFromErrorField = (value) => {
  if (typeof value === "string" && value.trim()) return value;
  if (!Array.isArray(value)) return null;
  return value.find((item) => typeof item === "string" && item.trim()) ?? null;
};

/**
 * Rejection copy is already actionable. A bad upload also sends field
 * errors; those say what to fix, so they win over the generic message.
 */
export const getScreenshotImportErrorMessage = (res) => {
  const errors = res?.data?.errors;
  if (errors && typeof errors === "object") {
    const fieldMessage =
      stringFromErrorField(errors.screenshot) ||
      stringFromErrorField(errors.table_data);
    if (fieldMessage) return fieldMessage;
  }
  const message = res?.data?.message;
  return typeof message === "string" && message.trim() ? message : null;
};

/**
 * Keep Time on every row so validate/import never drop an omitted OCR field.
 * A zoned screenshot clock is stored as UTC. A bare clock is left as sent.
 */
export const ensureActionedAtTimeOnRows = (rows) =>
  (Array.isArray(rows) ? rows : []).map((row) => {
    if (!row || typeof row !== "object") return row;
    const source =
      row.actioned_at_time != null && String(row.actioned_at_time).trim() !== ""
        ? row.actioned_at_time
        : row.actioned_at;
    const normalized =
      source == null || String(source).trim() === ""
        ? ""
        : normalizeActionedAtTimeToUtc(source) ?? String(source).trim();
    if (row.actioned_at_time === normalized) return row;
    return { ...row, actioned_at_time: normalized };
  });

export const loadTradeCaptureImportPrefs = () => {
  try {
    const raw = localStorage.getItem(TRADE_CAPTURE_IMPORT_PREFS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const saveTradeCaptureImportPrefs = (prefs) => {
  try {
    localStorage.setItem(TRADE_CAPTURE_IMPORT_PREFS_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore quota */
  }
};

const escapeCsvCell = (value) => {
  if (value == null) return "";
  const s = String(value);
  // Quote when the field contains a separator, a quote, or a line break.
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

/** Build CSV text with the grid's column headers (TRADE_COLUMNS by default; used by Trade Capture download + Quick Add Store). */
export const buildTradeCaptureCsv = (rows, columns = TRADE_COLUMNS) => {
  const header = columns.map((c) => escapeCsvCell(c.title)).join(",");
  const lines = (rows || []).map((row) =>
    columns.map((c) => escapeCsvCell(row?.[c.key])).join(",")
  );
  return [header, ...lines].join("\r\n");
};

const buildTimestampSlug = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}` +
    `-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`
  );
};

/** Trigger a client-side CSV download of the current trade-capture rows. */
export const downloadTradeCaptureTableAsCsv = (
  rows,
  { sessionId, columns } = {}
) => {
  const csv = buildTradeCaptureCsv(rows, columns);
  // BOM so Excel detects UTF-8 correctly.
  const blob = new Blob(["\uFEFF" + csv], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const idPart = sessionId ? `-${sessionId}` : "";
  const fileName = `trade-capture${idPart}-${buildTimestampSlug()}.csv`;
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const cancelTradeCaptureSessionInBackground = (sessionId) => {
  if (!sessionId) return;
  const baseURL = process.env.REACT_APP_BASE_URL || "";
  const url = `${baseURL.replace(/\/$/, "")}${SCREEN_CAPTURE_SESSIONS_END_POINT}/${sessionId}/cancel`;
  const token = getDataFromLocalStorage(GEEKS_USER)?.token;
  if (!token) return;
  try {
    fetch(url, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      keepalive: true,
      mode: "cors",
      credentials: "omit",
    }).catch(() => {});
  } catch {
    /* ignore */
  }
};

